import type { Dict, Mixpanel } from "mixpanel-browser";
import mixpanel from "mixpanel-browser";
import type { BaseAnalyticsServiceObject } from "../analytics.type";
import { additionalHosts } from "@certa/common/constants/hostIdentifierLoginConfig";
import { getHostName } from "@certa/network";

const defaultMixPanelHost = "https://api.mixpanel.com";
const token = import.meta.env.REACT_APP_MIXPANEL_PACKAGE_TOKEN;
const logAnalyticsInitFailure = () =>
  console.log("Unable to initialize Mixpanel Analytics Service.");

const initializeAnalytics = (): Mixpanel | undefined => {
  try {
    if (token) {
      const hostname = getHostName();
      const apiHost = additionalHosts.includes(hostname)
        ? `https://telemetry.${hostname}`
        : import.meta.env.REACT_APP_MIXPANEL_HOST || defaultMixPanelHost;
      const mixpanelInitializedInstance = mixpanel.init(
        token,
        {
          api_host: apiHost,
          // Track any URL changes in the path, query string, or hash
          track_pageview: "full-url"
        },
        "mixpanel-certa-analytics"
      );
      return mixpanelInitializedInstance;
    } else {
      logAnalyticsInitFailure();
    }
  } catch (e) {
    console.error(e);
    logAnalyticsInitFailure();
  }
  return undefined;
};

export const mixpanelAnalytics = (): BaseAnalyticsServiceObject => {
  const mixpanelInitializedInstance = initializeAnalytics();

  return {
    track: (name: string, props?: Dict) => {
      if (token && mixpanelInitializedInstance) {
        mixpanelInitializedInstance.track(name, props);
      } else {
        logAnalyticsInitFailure();
      }
    },
    identify: (email: string) => {
      if (token && mixpanelInitializedInstance) {
        mixpanelInitializedInstance.identify(email);
      } else {
        logAnalyticsInitFailure();
      }
    },
    register: (props: Dict) => {
      if (token && mixpanelInitializedInstance) {
        mixpanelInitializedInstance.register(props);
      } else {
        logAnalyticsInitFailure();
      }
    },
    reset: () => {
      if (token && mixpanelInitializedInstance) {
        mixpanelInitializedInstance.reset();
      } else {
        logAnalyticsInitFailure();
      }
    }
  } as const;
};
